import React, { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * AIChangeReview — panel "Usulan Perubahan" untuk AI Assistant mode Coding.
 *
 * Panel ini adalah gerbang manusia di antara AI dan file produksi. Yang harus
 * selalu terbaca sekali lihat oleh reviewer:
 *   1. file apa yang disentuh,
 *   2. apa persisnya yang berubah (diff berwarna),
 *   3. apakah usulannya lolos cek sintaks,
 *   4. apakah file sudah berubah sejak usulan dibuat (drift).
 *
 * Tanpa dependency baru — diff dirender dari teks unified diff apa adanya.
 */

const API = '/api/ai/changes';

const authHeaders = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function call(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const text = await res.text();
  let body;
  try { body = JSON.parse(text); } catch { body = { error: text.slice(0, 300) }; }
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body;
}

/* -------------------------------------------------------------------------- */

/* Setelah Terapkan: tawarkan build/restart, lalu laporkan hasilnya ke /api/agent-ops/report-deploy
   supaya Agent SDK bisa membacanya lewat tool last_deploy. */
async function afterApply(r) {
  const meta = (r && r.meta) || {};
  const abs = meta.abs || '';
  const rel = meta.rel || abs;
  const hint = (r && r.restart_hint) || {};
  let action = null;
  if (abs.includes('/dashboard/frontend/')) action = 'build';
  else if (hint.service === 'dashboard') action = 'restart-backend';
  else if (hint.service === 'ai-service') action = 'restart-ai-service';
  if (!action) { window.alert(`Applied.${hint.note ? '\n\nNext step: ' + hint.note : ''}`); return; }
  const label = {
    build: 'rebuild the frontend (about 2 minutes)',
    'restart-backend': 'restart the dashboard backend (auto-rollback if it does not come back up)',
    'restart-ai-service': 'restart ai-service',
  }[action];
  if (!window.confirm(`Applied: ${rel}\n\nRun now: ${label}?`)) {
    window.alert(`Applied. Remember to ${label} later.`);
    return;
  }
  const report = (ok, summary, log_tail) => call('/api/agent-ops/report-deploy', {
    method: 'POST', body: JSON.stringify({ proposal_id: r.id, file: rel, action, ok, summary, log_tail }),
  }).catch(() => {});
  try {
    if (action === 'build') {
      const res = await fetch('/api/file-deploy/build', { method: 'POST', headers: { ...authHeaders() } } /* tanpa body: Node 16 memicu req 'close' setelah body terbaca dan endpoint build lalu membunuh proses */);
      const log = await res.text();
      const m = log.match(/__BUILD_DONE__ exit=(\d+) durasi=(\d+)s/);
      const ok = !!m && m[1] === '0';
      const summary = m ? `Frontend build exit=${m[1]} in ${m[2]}s` : 'Frontend build ended without a completion marker';
      await report(ok, summary, log.slice(-20000));
      window.alert(`${ok ? '✅' : '❌'} ${summary}.${ok ? ' Reload the page to load the new frontend.' : ''}`);
    } else {
      const type = action === 'restart-backend' ? 'backend' : 'ai-service';
      await call('/api/file-deploy/restart', { method: 'POST', body: JSON.stringify({ type }) });
      let logs = null;
      for (let i = 0; i < 12 && !logs; i++) {
        await new Promise(ok => setTimeout(ok, 5000));
        try { logs = await call('/api/file-deploy/logs?lines=80'); } catch (_) { /* backend masih restart */ }
      }
      const ok = !!logs;
      const summary = ok ? `Restarted ${type}; the dashboard is reachable again` : `Restarted ${type}; the dashboard was not reachable after 60s (the watchdog may roll back)`;
      await report(ok, summary, (logs && logs.log) || '');
      window.alert(`${ok ? '✅' : '❌'} ${summary}.`);
    }
  } catch (e) {
    await report(false, 'Error: ' + e.message, '');
    window.alert('❌ ' + e.message);
  }
}

function DiffView({ diff }) {
  const lines = useMemo(() => String(diff || '').split('\n'), [diff]);
  if (!diff) return <div style={S.empty}>Tidak ada perbedaan untuk ditampilkan.</div>;
  return (
    <pre style={S.diff}>
      {lines.map((l, i) => {
        let bg = 'transparent', color = 'inherit';
        if (l.startsWith('+++') || l.startsWith('---')) { color = '#6b7280'; }
        else if (l.startsWith('@@'))  { bg = 'rgba(99,102,241,.12)'; color = '#4f46e5'; }
        else if (l.startsWith('+'))   { bg = 'rgba(22,163,74,.12)';  color = '#15803d'; }
        else if (l.startsWith('-'))   { bg = 'rgba(220,38,38,.12)';  color = '#b91c1c'; }
        return <div key={i} style={{ background: bg, color, padding: '0 8px' }}>{l || ' '}</div>;
      })}
    </pre>
  );
}

function Badge({ ok, children }) {
  return (
    <span style={{ ...S.badge, background: ok ? 'rgba(22,163,74,.12)' : 'rgba(220,38,38,.12)', color: ok ? '#15803d' : '#b91c1c' }}>
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------- */

export default function AIChangeReview({ onClose, onApplied }) {
  const [status, setStatus]   = useState('pending');
  const [items, setItems]     = useState([]);
  const [applyEnabled, setAE] = useState(true);
  const [sel, setSel]         = useState(null);      // detail usulan terpilih
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');
  const [note, setNote]       = useState('');

  const load = useCallback(async (st = status) => {
    setErr('');
    try {
      const r = await call(`${API}?status=${encodeURIComponent(st)}`);
      setItems(r.items || []);
      setAE(r.apply_enabled !== false);
    } catch (e) { setErr(e.message); }
  }, [status]);

  useEffect(() => { load(status); }, [status, load]);

  const open = async (id) => {
    setErr(''); setSel(null);
    try { setSel(await call(`${API}/${id}`)); }
    catch (e) { setErr(e.message); }
  };

  const act = async (verb, body) => {
    if (!sel) return;
    // Apply menulis ke file yang dipakai aplikasi berjalan. Satu konfirmasi
    // eksplisit lebih murah daripada satu insiden produksi.
    if (verb === 'apply' && !window.confirm(`Terapkan perubahan ke ${sel.meta.rel}?\n\nFile akan di-backup otomatis dan bisa di-rollback.`)) return;
    if (verb === 'rollback' && !window.confirm(`Kembalikan ${sel.meta.rel} ke versi sebelum perubahan ini?`)) return;
    setBusy(true); setErr('');
    try {
      const r = await call(`${API}/${sel.meta.id}/${verb}`, { method: 'POST', body: JSON.stringify(body || {}) });
      setSel(null); setNote('');
      await load(status);
      if (verb === 'apply' && onApplied) onApplied(r);
      if (verb === 'apply') await afterApply(r);
      else if (r.restart_hint && r.restart_hint.note) window.alert(`Berhasil.\n\nLangkah berikutnya: ${r.restart_hint.note}`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <strong>Usulan Perubahan Kode</strong>
        <div style={S.tabs}>
          {['pending', 'applied', 'rejected'].map(s => (
            <button key={s} onClick={() => { setSel(null); setStatus(s); }}
              style={{ ...S.tab, ...(status === s ? S.tabOn : {}) }}>
              {{ pending: 'Menunggu', applied: 'Diterapkan', rejected: 'Ditolak' }[s]}
            </button>
          ))}
        </div>
        <button onClick={() => load(status)} style={S.link}>Muat ulang</button>
        {onClose && <button onClick={onClose} style={S.link}>Tutup</button>}
      </div>

      {!applyEnabled && (
        <div style={S.warn}>Penerapan perubahan sedang dibekukan oleh administrator (AI_APPLY_ENABLED=false).</div>
      )}
      {err && <div style={S.error}>{err}</div>}

      <div style={S.body}>
        <div style={S.list}>
          {items.length === 0 && <div style={S.empty}>Tidak ada usulan.</div>}
          {items.map(it => (
            <button key={it.id} onClick={() => open(it.id)}
              style={{ ...S.row, ...(sel && sel.meta.id === it.id ? S.rowOn : {}) }}>
              <div style={S.rowTop}>
                <code style={S.path}>{it.rel}</code>
                <Badge ok={it.check && it.check.ok}>{it.check && it.check.ok ? 'sintaks ok' : 'sintaks gagal'}</Badge>
              </div>
              <div style={S.rowSub}>
                <span style={{ color: '#15803d' }}>+{it.stat.added}</span>{' '}
                <span style={{ color: '#b91c1c' }}>-{it.stat.removed}</span>
                {' · '}{new Date(it.created_at).toLocaleString('id-ID')}
              </div>
              {it.reason && <div style={S.reason}>{it.reason}</div>}
            </button>
          ))}
        </div>

        <div style={S.detail}>
          {!sel && <div style={S.empty}>Pilih satu usulan untuk melihat diff-nya.</div>}
          {sel && (
            <>
              <div style={S.detailHead}>
                <code style={S.path}>{sel.meta.rel}</code>
                <span style={S.meta}>
                  {sel.meta.kind === 'create' ? 'file baru' : 'ubah file'} · {sel.meta.bytes} byte
                </span>
              </div>
              {sel.meta.reason && <div style={S.reasonBig}>{sel.meta.reason}</div>}

              {!sel.meta.check.ok && (
                <div style={S.error}>
                  Cek sintaks GAGAL ({sel.meta.check.checker}):
                  <pre style={S.pre}>{sel.meta.check.output}</pre>
                </div>
              )}
              {sel.drifted && sel.status === 'pending' && (
                <div style={S.warn}>
                  File di server sudah berubah setelah usulan ini dibuat. Diff di bawah dihitung
                  terhadap isi TERBARU — periksa baik-baik sebelum menerapkan.
                </div>
              )}

              <DiffView diff={sel.diff} />

              {sel.status === 'pending' && (
                <div style={S.actions}>
                  <input value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Catatan (opsional, untuk penolakan)" style={S.input} />
                  <button disabled={busy || !applyEnabled} onClick={() => act('apply')} style={S.btnPrimary}>
                    {busy ? 'Memproses…' : 'Terapkan'}
                  </button>
                  <button disabled={busy} onClick={() => act('reject', { note })} style={S.btn}>Tolak</button>
                </div>
              )}
              {sel.status === 'applied' && sel.meta.status === 'applied' && (
                <div style={S.actions}>
                  <span style={S.meta}>
                    Diterapkan {new Date(sel.meta.applied_at).toLocaleString('id-ID')} oleh {sel.meta.applied_by}
                  </span>
                  <button disabled={busy} onClick={() => act('rollback')} style={S.btn}>Rollback</button>
                </div>
              )}
              {sel.meta.status === 'rolled_back' && (
                <div style={S.meta}>Sudah di-rollback {new Date(sel.meta.rolled_back_at).toLocaleString('id-ID')}.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const S = {
  wrap:   { border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, overflow: 'hidden', background: 'var(--card, #fff)' },
  head:   { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border, #e5e7eb)' },
  tabs:   { display: 'flex', gap: 4, marginLeft: 8 },
  tab:    { border: '1px solid transparent', background: 'transparent', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 13 },
  tabOn:  { background: 'rgba(99,102,241,.12)', color: '#4f46e5', borderColor: 'rgba(99,102,241,.3)' },
  link:   { marginLeft: 'auto', border: 0, background: 'transparent', color: '#4f46e5', cursor: 'pointer', fontSize: 13 },
  body:   { display: 'grid', gridTemplateColumns: 'minmax(220px, 320px) 1fr', minHeight: 280 },
  list:   { borderRight: '1px solid var(--border, #e5e7eb)', maxHeight: 520, overflowY: 'auto' },
  row:    { display: 'block', width: '100%', textAlign: 'left', border: 0, borderBottom: '1px solid var(--border, #f1f5f9)', background: 'transparent', padding: '10px 12px', cursor: 'pointer' },
  rowOn:  { background: 'rgba(99,102,241,.08)' },
  rowTop: { display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' },
  rowSub: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  reason: { fontSize: 12, color: '#374151', marginTop: 4 },
  reasonBig: { fontSize: 13, color: '#374151', padding: '6px 12px' },
  path:   { fontFamily: mono, fontSize: 12 },
  badge:  { fontSize: 11, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' },
  detail: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  detailHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--border, #f1f5f9)' },
  meta:   { fontSize: 12, color: '#6b7280' },
  diff:   { margin: 0, padding: '8px 0', fontFamily: mono, fontSize: 12, lineHeight: 1.5, overflowX: 'auto', flex: 1, maxHeight: 400 },
  pre:    { margin: '6px 0 0', whiteSpace: 'pre-wrap', fontFamily: mono, fontSize: 12 },
  actions: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, borderTop: '1px solid var(--border, #f1f5f9)' },
  input:  { flex: 1, minWidth: 0, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border, #e5e7eb)', fontSize: 13 },
  btn:    { padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border, #e5e7eb)', background: 'transparent', cursor: 'pointer', fontSize: 13 },
  btnPrimary: { padding: '6px 16px', borderRadius: 6, border: 0, background: '#4f46e5', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  empty:  { padding: 16, color: '#6b7280', fontSize: 13 },
  warn:   { margin: 12, padding: '8px 12px', borderRadius: 6, background: 'rgba(217,119,6,.12)', color: '#b45309', fontSize: 13 },
  error:  { margin: 12, padding: '8px 12px', borderRadius: 6, background: 'rgba(220,38,38,.12)', color: '#b91c1c', fontSize: 13 },
};
